import {
  AlertModal,
  Button,
  Collapsible,
  Flex, FlexColumn, Icon, IconButton,
  isUserWithRoles,
  Tooltip,
  useAsyncFunctionProgress,
  useCombinedUser,
  useDownloadFile,
  useIsMsalAuth,
  useIsUserAuth,
  UserRoles,
  useStyledTheme,
  useWarningAlert,
} from '@equifinance/shared';
import styled, { css } from 'styled-components';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import Markdown from 'react-markdown';
import { DownloadIcon, DownloadLink } from '../../Products/ProductComponents';
import {
  DecisionOutcome, Task, useTasksContext,
} from '../../../contexts/tasks';
import UnlockIcon from '../../Icons/UnlockIcon';
import UnderwriterDecision from './UnderwriterDecision';
import TaskStatusLabel from './TaskStatusLabel';
import TaskStatusIcon from './TaskStatusIcon';
import TaskContent from './TaskContent';
import InternalNotes from './InternalNotes';
import TaskCreation from './TaskCreation';
import { usePageConfigurationContext } from '../../../contexts/pageConfiguration';
import useDocumentsApi, { TemplateCategory } from '../../../api/useDocumentsApi';
import { useAccountContext } from '../../../contexts';
import { HiddedA } from './Evidence/DocumentaryEvidenceItem';
import ChangeStageModal from './ChangeStageModal';

const TaskHeaderCss = css`${({ theme }) => `
  background: ${theme.colours.primary[11]};
  border-radius: unset;
  padding: 0;
  padding-right: ${theme.padding[3]};

  .mi-chevron-down, .mi-chevron-up {
    margin-left: 0;
  }
`}`;

const TaskHeader = styled(Flex)`${({ theme }) => `
  width: 100%;
  > div {
    margin: ${theme.padding[3]} 0;
  }
`}`;

const TaskStatus = styled(Flex) <{ updated?: boolean }>`
  ${({ theme, updated }) => `
    margin: 0 !important;
    width: ${theme.taskStatusWidth};
    display: flex;
    justify-content:center;
    align-items: center;
    font-size: ${theme.fontSize[1]};
    ${updated && `
      background-color: ${theme.colours.danger[2]};
    `};
  `}
`;

const TaskDetails = styled(FlexColumn) <{ updated: boolean }>`${({ theme, updated }) => `
  padding: 0 ${theme.padding[6]};
  font-size: ${theme.fontSize[1]};
  gap: ${theme.padding[4]};
  ${!updated && `
    border-left: 1px solid ${theme.colours.neutral[7]};
  `};
  flex: 1;
`}`;

const TaskTitle = styled.div`${({ theme }) => `
  font-size: ${theme.fontSize[1]};
  font-weight: ${theme.fontWeight.bold};
`}`;

const TaskDescription = styled.div`${({ theme }) => `
  font-size: ${theme.fontSize[1]};
  font-weight: ${theme.fontWeight.normal};
`}`;

const TaskTimeUpdated = styled.div`${({ theme }) => `
  font-size: ${theme.fontSize[1]};
  font-weight: ${theme.fontWeight.normal};
  padding-right: ${theme.padding[6]};
`}`;

const TaskDownloadTemplate = styled.div`${({ theme }) => `
  padding-left: ${theme.padding[6]};
  border-left: 1px solid ${theme.colours.neutral[7]};
`}`;

const TaskStage = styled(FlexColumn)`${({ theme }) => `
  padding: 0 ${theme.padding[6]};
  border-left: 1px solid ${theme.colours.neutral[7]};
  font-size: ${theme.fontSize[1]};
  justify-content: space-between;
  width: ${theme.taskStageWidth};
`}`;

const TaskStageLabel = styled(Flex)`${({ theme }) => `
  gap: ${theme.padding[1]};
`}`;

const TaskStageLabelName = styled.p`${({ theme }) => `
  font-weight: ${theme.fontWeight.normal};
  font-size: ${theme.fontSize[1]};
  text-transform: uppercase;
  color: ${theme.colours.primary[1]};
`}`;

const TaskStageLabelValue = styled.p`${({ theme }) => `
  font-weight: ${theme.fontWeight.bold};
  font-size: ${theme.fontSize[1]};
  color: ${theme.colours.primary[1]};
`}`;

const TaskLock = styled(Flex) <{ hidden?: boolean }>`${({ theme, hidden = false }) => `
  justify-content: center;
  align-items: center;
  padding: 0 ${theme.padding[3]};
  border-left: 1px solid ${theme.colours.neutral[7]};
  > * {
    visibility: ${hidden ? 'hidden' : 'visible'}; 
  }
`}`;

export const TaskUnlockIcon = styled(Icon).attrs(() => ({
  className: 'mi-unlock',
}))` ${({ theme }) => `
  color: ${theme.colours.primary[1]};
  font-size:${theme.fontSize[3]};
`}`;

const bodyContentCss = css`${({ theme }) => `
  width: auto;
  padding: 0 ${theme.padding[6]};
  gap: ${theme.padding[6]};
`}`;

const bodyCss = css`${({ theme }) => `
  background-color: ${theme.colours.neutral[9]};
`}`;

const SubmitTaskBtn = styled(Button)`
  width: fit-content;
  align-self: end;
`;

const TaskPencilIcon = styled(Icon).attrs(() => ({
  className: 'mi-edit',
}))` ${({ theme }) => `
  color: ${theme.colours.primary[1]};
  font-size:${theme.fontSize[3]};
`}`;

export default function TaskDetailComponent({ task, taskIndex }: { task: Task, taskIndex: number }) {
  const warningAlert = useWarningAlert();
  const theme = useStyledTheme();
  const {
    expandedStates, handleToggle, unklockTask, readTask, submitToLender, underwriterSubmission,
  } = useTasksContext();
  const { isReadOnly } = usePageConfigurationContext();
  const { postDocumentTemplate } = useDocumentsApi();
  const { id: applicationId } = useAccountContext();
  const {
    downloadFile, downloadFileRef,
  } = useDownloadFile();

  const [selectedDecision, setSelectedDecision] = useState(task?.currentUnderwritingDecision?.outcome);
  const onChangeDecision = (value: any) => {
    setSelectedDecision(value);
  };

  const isDraftTask = task.status === 'Draft';

  const { user } = useCombinedUser();
  const isInternalUser = useIsMsalAuth();
  const isUnderwriter = isInternalUser && isUserWithRoles([UserRoles.UNDERWRITER], user);
  const isSalesSupport = isInternalUser && isUserWithRoles([UserRoles.SALES_SUPPORT], user);
  const isExternalUser = useIsUserAuth();

  const showSubmitBtn = isUnderwriter || isSalesSupport || isExternalUser;

  const canSubmitTask = useMemo(() => {
    if (isExternalUser || isSalesSupport) {
      return ['OutstandingForBroker', 'MoreInformationRequired', 'Rejected'].includes(task.status)
        && (task?.documentEvidence?.length || task?.writtenEvidence?.length);
    }

    if (isUnderwriter) {
      return task.status === 'SubmittedToLender' && !!selectedDecision;
    }

    return false;
  }, [isExternalUser, isUnderwriter, isSalesSupport, task, selectedDecision]);

  const onSubmitTask = () => {
    if (!canSubmitTask) return;

    if (isExternalUser || isSalesSupport) {
      submitToLender(task.id);
      return;
    }

    if (isUnderwriter) {
      underwriterSubmission({
        taskId: task.id,
        decisionOutcome: selectedDecision,
        decisionOutcomeDetail: DecisionOutcome[selectedDecision],
        decisionOutcomeReason: DecisionOutcome[selectedDecision],
      });
    }
  };

  const onUnlock = (e: any) => {
    e.stopPropagation();

    warningAlert({
      onConfirm: () => { unklockTask(task.id); },
      title: 'Unlock',
      content: 'Are you sure you want to unlock this task for the broker to edit?',
      confirmLabel: 'Unlock',
      titleIconOverride: <UnlockIcon fill={theme.colours.primary[1]} />,
    });
  };

  const onDownloadTemplates = async () => {
    const templates = await Promise.all((task.templateTypes ?? []).map((tt) => postDocumentTemplate(applicationId, {
      templateTypeId: tt,
      category: task.category as TemplateCategory,
    })));

    templates.forEach(({ fileName, documentData }) => {
      if (fileName && documentData) {
        downloadFile(fileName, documentData);
      }
    });
  };

  const { inProgress, execute: executeDownloadTemplates } = useAsyncFunctionProgress(onDownloadTemplates);

  useEffect(() => {
    if (task.updated && expandedStates[task.id] && !isReadOnly) {
      readTask(task.id);
    }
  }, [expandedStates[task.id]]);

  const canChangeStage = useMemo(() => isUnderwriter && !['Draft', 'Approved', 'ApprovedWithException', 'Rejected'].includes(task.status), [task.status, isUnderwriter]);

  const [showChangeStage, setShowChangeStage] = useState(false);

  const openChangeStageModal = (e: any) => {
    e.stopPropagation();
    setShowChangeStage(true);
  };

  const closeChangeStageModal = () => {
    setShowChangeStage(false);
  };

  return (
    <>
      <Collapsible
        id="task-details"
        level={2}
        headerCss={TaskHeaderCss}
        collapsed={!expandedStates[task.id]}
        onClickCollapse={() => handleToggle(task.id)}
        bodyContentCss={bodyContentCss}
        bodyCss={bodyCss}
        title={(
          <TaskHeader>
            <TaskStatus updated={task.updated}>
              <TaskStatusIcon status={task.status} updated={task.updated} />
            </TaskStatus>
            <TaskDetails updated={task.updated}>
              <FlexColumn>
                <TaskTitle>
                  {task.title || '-'}
                </TaskTitle>
                <TaskDescription>
                  {task.description ? (
                    <Markdown>
                      {task.description}
                    </Markdown>
                  ) : '-'}
                </TaskDescription>
              </FlexColumn>
              <Flex>
                <TaskTimeUpdated>
                  {`Last updated: ${moment(task.lastUpdated).format('DD/MM/YY, HH:mm:ss')}`}
                </TaskTimeUpdated>
                {task.templateTypes?.length > 0 && (
                  <TaskDownloadTemplate>
                    <HiddedA ref={downloadFileRef} onClick={(e) => { e.stopPropagation(); }} />
                    <DownloadIcon className="mi-download" disabled={inProgress} />
                    <DownloadLink
                      onClick={(e) => {
                        e.stopPropagation();
                        if (inProgress) return;
                        executeDownloadTemplates();
                      }}
                      aria-hidden="true"
                      disabled={inProgress}
                    >
                      Download template(s)
                    </DownloadLink>
                  </TaskDownloadTemplate>
                )}
              </Flex>
            </TaskDetails>
            <TaskStage>
              <TaskStatusLabel status={task.status} />
              <TaskStageLabel>
                <TaskStageLabelName>before</TaskStageLabelName>
                <TaskStageLabelValue>{task.stage || '-'}</TaskStageLabelValue>
                {canChangeStage && <IconButton onClick={openChangeStageModal}><TaskPencilIcon /></IconButton>}
              </TaskStageLabel>
            </TaskStage>
            <TaskLock hidden={!isUnderwriter || task.status !== 'SubmittedToLender'}>
              <IconButton onClick={onUnlock} id={`unlock-${task.id}`} disabled={isReadOnly}>
                <TaskUnlockIcon />
              </IconButton>
              <Tooltip
                id={`unlock-${task.id}`}
                html="Unlock this task for the Broker."
                place="top"
              />
            </TaskLock>
          </TaskHeader>
        )}
      >
        {
          isDraftTask ? <TaskCreation task={task} taskIndex={taskIndex} /> : (
            <>
              <TaskContent task={task} taskIndex={taskIndex} />
              {isUnderwriter && <UnderwriterDecision task={task} selectedDecision={selectedDecision} onChangeDecision={onChangeDecision} disabled={task.status !== 'SubmittedToLender'} />}
              {showSubmitBtn && <SubmitTaskBtn disabled={!canSubmitTask} small onClick={onSubmitTask}>{isUnderwriter ? 'Submit' : 'Submit task'}</SubmitTaskBtn>}
              {isUnderwriter && <InternalNotes taskId={task.id} notes={task.privateNotes} />}
            </>
          )
        }
      </Collapsible>
      <AlertModal
        title="Change before stage"
        isOpen={showChangeStage}
        onClose={closeChangeStageModal}
        content={<ChangeStageModal taskIndex={taskIndex} close={closeChangeStageModal} />}
        showButtons={false}
      />
    </>
  );
}
